/**
 * useTimeSeriesCalculations Hook
 *
 * Provides calculation utilities for time series financial data.
 * Includes YoY growth, TTM values, and acceleration calculations.
 */
import { useCallback } from 'react';
import { PERCENT_FIELD_TO_RAW_FIELD } from '../configs/keyMetrics';
import { getTrajectoryMetrics } from '../utils';
const { ttmMetrics: TRAJECTORY_TTM_METRICS } = getTrajectoryMetrics();

/**
 * Custom hook for time series calculations on financial data.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.alignment - 'annual' or 'quarterly'
 * @param {Array} options.annualData - Annual financial data array
 * @param {Array} options.qtrData - Quarterly financial data array
 * @param {Array} options.annualMapping - Annual period mapping array
 * @param {Array} options.qtrMapping - Quarterly period mapping array
 * @returns {Object} Calculation functions and utilities
 */
export function useTimeSeriesCalculations({
  alignment = 'annual',
  annualData = [],
  qtrData = [],
  annualMapping = [],
  qtrMapping = [],
}) {
  // Get current data and mapping based on alignment
  const currentData = alignment === 'annual' ? annualData : qtrData;
  const currentMapping = alignment === 'annual' ? annualMapping : qtrMapping;

  /**
   * Calculate TTM (trailing twelve months) values by summing last 4 quarters.
   */
  const calculateTTMValues = useCallback((data, mapping, sourceField) => {
    const fieldData = data.find(item => item.field === sourceField);
    if (!fieldData) return {};

    const periods = mapping.map(m => m.period);
    const ttmValues = {};

    // Need at least 4 quarters to calculate TTM
    for (let i = 3; i < periods.length; i++) {
      const q1 = fieldData[periods[i - 3]];
      const q2 = fieldData[periods[i - 2]];
      const q3 = fieldData[periods[i - 1]];
      const q4 = fieldData[periods[i]];

      // Only calculate if all 4 quarters have values
      if (q1 != null && q2 != null && q3 != null && q4 != null) {
        ttmValues[periods[i]] = q1 + q2 + q3 + q4;
      }
    }

    return ttmValues;
  }, []);

  /**
   * Calculate YoY growth rates for a given field.
   */
  const calculateYoYGrowth = useCallback((data, mapping, field) => {
    // Check if this is a TTM calculated metric
    const ttmMetric = TRAJECTORY_TTM_METRICS.find(m => m.field === field);

    let fieldValues;
    if (ttmMetric) {
      // For TTM metrics, calculate TTM values from source field
      const ttmValues = calculateTTMValues(data, mapping, ttmMetric.sourceField);
      fieldValues = { field, ...ttmValues };
    } else {
      fieldValues = data.find(item => item.field === field);
    }

    if (!fieldValues) return [];

    const results = [];
    const periods = mapping.map(m => m.period);
    const isQuarterly = mapping.some(m => m.period?.includes('Q'));
    const lookback = isQuarterly ? 4 : 1;

    for (let i = lookback; i < periods.length; i++) {
      const currentPeriod = periods[i];
      const priorPeriod = periods[i - lookback];
      const currentVal = fieldValues[currentPeriod];
      const priorVal = fieldValues[priorPeriod];

      if (currentVal != null && priorVal != null && priorVal !== 0) {
        const yoyGrowth = (currentVal - priorVal) / Math.abs(priorVal);
        results.push({
          period: currentPeriod,
          yoyGrowth,
          currentVal,
          priorVal,
        });
      }
    }

    return results;
  }, [calculateTTMValues]);

  /**
   * Get field value from data, handling percentage fields that need calculation.
   */
  const getFieldValue = useCallback((data, field, period) => {
    // Check if this is a percentage field that needs calculation
    const rawFieldName = PERCENT_FIELD_TO_RAW_FIELD[field];
    if (rawFieldName) {
      // Get the raw value and revenue, then calculate percentage
      const rawFieldData = data.find(item => item.field === rawFieldName);
      const revenueData = data.find(item => item.field === 'Revenue');
      if (!rawFieldData || !revenueData) return null;

      const rawValue = rawFieldData[period];
      const revenue = revenueData[period];
      if (rawValue == null || revenue == null || revenue === 0) return null;

      return rawValue / revenue;
    }

    // For other fields, just return the value directly
    const fieldData = data.find(item => item.field === field);
    return fieldData ? fieldData[period] : null;
  }, []);

  /**
   * Get YoY prior period for a given current period.
   */
  const getYoYPriorPeriod = useCallback((currentPeriod, mapping, isAnnual) => {
    if (!currentPeriod) return null;

    // For annual data (e.g., FY24 -> FY23)
    if (isAnnual) {
      const match = currentPeriod.match(/FY(\d+)/);
      if (match) {
        const currentYear = parseInt(match[1], 10);
        const priorYear = currentYear - 1;
        const priorPeriod = `FY${priorYear}`;
        return mapping.find(m => m.period === priorPeriod)?.period || null;
      }
    }

    // For quarterly data - handle format "Q4-20" (Q[quarter]-[year])
    const qtrMatch = currentPeriod.match(/Q(\d)-(\d+)/);
    if (qtrMatch) {
      const quarter = qtrMatch[1];
      const currentYear = parseInt(qtrMatch[2], 10);
      const priorYear = currentYear - 1;
      const priorPeriod = `Q${quarter}-${priorYear}`;
      return mapping.find(m => m.period === priorPeriod)?.period || null;
    }

    // Also try format "Q1 FY24" just in case
    const qtrFYMatch = currentPeriod.match(/Q(\d)\s*FY(\d+)/);
    if (qtrFYMatch) {
      const quarter = qtrFYMatch[1];
      const currentYear = parseInt(qtrFYMatch[2], 10);
      const priorYear = currentYear - 1;
      const priorPeriod = `Q${quarter} FY${priorYear}`;
      return mapping.find(m => m.period === priorPeriod)?.period || null;
    }

    return null;
  }, []);

  /**
   * Calculate acceleration data (change in YoY growth rate).
   * Returns rows for table display with periods as columns.
   */
  const calculateAccelerationData = useCallback((validPeriods, metrics = []) => {
    // Calculate YoY for Revenue
    const revenueYoY = calculateYoYGrowth(currentData, currentMapping, 'Revenue');

    // Build rows: Revenue YoY, Revenue Accel, then each compare metric's YoY and Accel
    const rows = [];

    // Revenue YoY row
    const revenueYoYRow = { metric: 'Revenue YoY%', type: 'yoy', field: 'Revenue' };
    validPeriods.forEach(period => {
      const data = revenueYoY.find(d => d.period === period);
      revenueYoYRow[period] = data?.yoyGrowth ?? null;
    });
    rows.push(revenueYoYRow);

    // Revenue Acceleration row
    const revenueAccelRow = { metric: 'Revenue Accel', type: 'accel', field: 'Revenue' };
    validPeriods.forEach((period) => {
      const currentDataPoint = revenueYoY.find(d => d.period === period);
      const priorIdx = revenueYoY.findIndex(d => d.period === period) - 1;
      const priorData = priorIdx >= 0 ? revenueYoY[priorIdx] : null;
      revenueAccelRow[period] = currentDataPoint && priorData
        ? currentDataPoint.yoyGrowth - priorData.yoyGrowth
        : null;
    });
    rows.push(revenueAccelRow);

    // Add rows for each selected comparison metric
    metrics.forEach(({ field: metricField, label }) => {
      if (metricField === 'Revenue') return; // Skip if Revenue is selected

      const metricYoY = calculateYoYGrowth(currentData, currentMapping, metricField);

      // YoY row
      const yoyRow = { metric: `${label} YoY%`, type: 'yoy', field: metricField };
      validPeriods.forEach(period => {
        const data = metricYoY.find(d => d.period === period);
        yoyRow[period] = data?.yoyGrowth ?? null;
      });
      rows.push(yoyRow);

      // Acceleration row
      const accelRow = { metric: `${label} Accel`, type: 'accel', field: metricField };
      validPeriods.forEach(period => {
        const currentDataPoint = metricYoY.find(d => d.period === period);
        const priorIdx = metricYoY.findIndex(d => d.period === period) - 1;
        const priorData = priorIdx >= 0 ? metricYoY[priorIdx] : null;
        accelRow[period] = currentDataPoint && priorData
          ? currentDataPoint.yoyGrowth - priorData.yoyGrowth
          : null;
      });
      rows.push(accelRow);
    });

    return rows;
  }, [calculateYoYGrowth, currentData, currentMapping]);

  /**
   * Prepare margin bridge data for waterfall chart.
   * @param {Array} slicedMapping - Available periods
   * @param {string} comparison - 'yoy' or 'qoq' (used if explicit periods not provided)
   * @param {string} target - 'operating' or 'fcf'
   * @param {string} explicitPriorPeriod - Optional explicit prior period
   * @param {string} explicitCurrentPeriod - Optional explicit current period
   */
  const calculateMarginBridgeData = useCallback((slicedMapping, comparison = 'yoy', target = 'operating', explicitPriorPeriod = null, explicitCurrentPeriod = null) => {
    // Use explicit periods if provided, otherwise calculate based on comparison type
    let latestPeriod = explicitCurrentPeriod;
    let priorPeriod = explicitPriorPeriod;

    if (!latestPeriod) {
      // Get latest period from mapping
      latestPeriod = slicedMapping[slicedMapping.length - 1]?.period;
    }
    if (!latestPeriod) return { bars: [], latestPeriod: null, priorPeriod: null };

    if (!priorPeriod) {
      // Get comparison period based on selection
      if (comparison === 'yoy') {
        priorPeriod = getYoYPriorPeriod(latestPeriod, currentMapping, alignment === 'annual');
      } else {
        // QoQ: use previous period in the sliced mapping
        priorPeriod = slicedMapping[slicedMapping.length - 2]?.period;
      }
    }

    if (!priorPeriod) return { bars: [], latestPeriod, priorPeriod: null };

    // Get values for both periods
    const current = {
      gm: getFieldValue(currentData, 'GM % of Sales', latestPeriod),
      sm: getFieldValue(currentData, 'S&M % of Sales', latestPeriod),
      rd: getFieldValue(currentData, 'R&D % of Sales', latestPeriod),
      ga: getFieldValue(currentData, 'G&A % of Sales', latestPeriod),
      fcf: getFieldValue(currentData, 'FCF % of Sales', latestPeriod),
    };
    current.opMargin = current.gm != null ? current.gm - ((current.sm || 0) + (current.rd || 0) + (current.ga || 0)) : null;

    const prior = {
      gm: getFieldValue(currentData, 'GM % of Sales', priorPeriod),
      sm: getFieldValue(currentData, 'S&M % of Sales', priorPeriod),
      rd: getFieldValue(currentData, 'R&D % of Sales', priorPeriod),
      ga: getFieldValue(currentData, 'G&A % of Sales', priorPeriod),
      fcf: getFieldValue(currentData, 'FCF % of Sales', priorPeriod),
    };
    prior.opMargin = prior.gm != null ? prior.gm - ((prior.sm || 0) + (prior.rd || 0) + (prior.ga || 0)) : null;

    // Check if we have enough data
    if (current.gm == null || prior.gm == null) {
      return { bars: [], latestPeriod, priorPeriod };
    }

    // Calculate changes (note: for OpEx, DECREASE is positive contribution)
    const deltaGM = current.gm - prior.gm;
    const deltaSM = (prior.sm || 0) - (current.sm || 0);  // Inverted: lower is better
    const deltaRD = (prior.rd || 0) - (current.rd || 0);
    const deltaGA = (prior.ga || 0) - (current.ga || 0);

    // Build change bridge bars
    const bars = [];

    if (target === 'operating') {
      bars.push(
        { category: `Prior Op. Margin`, value: prior.opMargin, type: 'start' },
        { category: 'Gross Margin', value: deltaGM, type: 'change' },
        { category: 'S&M', value: deltaSM, type: 'change' },
        { category: 'R&D', value: deltaRD, type: 'change' },
        { category: 'G&A', value: deltaGA, type: 'change' },
        { category: `Current Op. Margin`, value: current.opMargin, type: 'end' },
      );
    } else {
      // FCF bridge includes OM→FCF delta change
      const priorOMtoFCF = (prior.fcf || 0) - (prior.opMargin || 0);
      const currentOMtoFCF = (current.fcf || 0) - (current.opMargin || 0);
      const deltaOMtoFCF = currentOMtoFCF - priorOMtoFCF;

      bars.push(
        { category: `Prior FCF Margin`, value: prior.fcf, type: 'start' },
        { category: 'Gross Margin', value: deltaGM, type: 'change' },
        { category: 'S&M', value: deltaSM, type: 'change' },
        { category: 'R&D', value: deltaRD, type: 'change' },
        { category: 'G&A', value: deltaGA, type: 'change' },
        { category: 'Other (OM→FCF)', value: deltaOMtoFCF, type: 'change' },
        { category: `Current FCF Margin`, value: current.fcf, type: 'end' },
      );
    }

    return { bars, latestPeriod, priorPeriod, current, prior };
  }, [alignment, currentData, currentMapping, getFieldValue, getYoYPriorPeriod]);

  /**
   * Prepare margin table data for display.
   */
  const calculateMarginTableData = useCallback((slicedMapping) => {
    return slicedMapping.map(m => {
      const period = m.period;
      const gm = getFieldValue(currentData, 'GM % of Sales', period);
      const sm = getFieldValue(currentData, 'S&M % of Sales', period);
      const rd = getFieldValue(currentData, 'R&D % of Sales', period);
      const ga = getFieldValue(currentData, 'G&A % of Sales', period);
      const fcf = getFieldValue(currentData, 'FCF % of Sales', period);

      // Calculate operating margin (GM - total OpEx)
      const totalOpex = (sm || 0) + (rd || 0) + (ga || 0);
      const opMargin = gm != null ? gm - totalOpex : null;

      return {
        period,
        grossMargin: gm,
        salesMarketing: sm,
        researchDev: rd,
        generalAdmin: ga,
        totalOpex,
        operatingMargin: opMargin,
        fcfMargin: fcf,
      };
    }).filter(d => d.grossMargin != null);
  }, [currentData, getFieldValue]);

  return {
    // Calculation functions
    calculateTTMValues,
    calculateYoYGrowth,
    getFieldValue,
    getYoYPriorPeriod,
    calculateAccelerationData,
    calculateMarginBridgeData,
    calculateMarginTableData,

    // Current data references
    currentData,
    currentMapping,
  };
}

export default useTimeSeriesCalculations;
